import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface CartItem {
  id: string; // productId
  title: string;
  quantity: number;
  unit_price: number; // dollars
  thumbnail?: string;
  metadata?: { vendor_id?: string; vendor_name?: string };
}

interface Cart {
  id: string;
  vendorSlug: string;
  vendorName: string;
  items: CartItem[];
  total: number;
  subtotal: number;
}

interface AddProductInput {
  productId: string;
  name: string;
  price: number; // dollars
  image?: string;
  vendorSlug: string;
  vendorName: string;
}

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  addItem: (input: AddProductInput, quantity?: number) => void;
  updateItem: (itemId: string, quantity: number) => void;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
  itemCount: number;
  // Mini slide-out cart
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const STORAGE_KEY = 'nicherest_cart';

function totalsOf(items: CartItem[]) {
  const sum = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  return { total: sum, subtotal: sum };
}

function loadCart(): Cart | null {
  try {
    if (typeof localStorage === 'undefined') return null; // SSR: no cart on the server
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || !Array.isArray(c.items)) return null;
    return c;
  } catch {
    return null;
  }
}

function saveCart(cart: Cart | null) {
  try {
    if (cart && cart.items.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const CartContext = createContext<CartContextType>({
  cart: null,
  loading: false,
  addItem: () => {},
  updateItem: () => {},
  removeItem: () => {},
  clearCart: () => {},
  itemCount: 0,
  isCartOpen: false,
  openCart: () => {},
  closeCart: () => {},
});

export function CartProvider({ children }: { children: React.ReactNode }) {
  // Always start with null to match SSR. Load from localStorage after mount so
  // the initial render is identical on server and client — prevents hydration mismatch.
  const [cart, setCart] = useState<Cart | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    const saved = loadCart();
    if (saved) setCart(saved);
  }, []);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  const addItem = useCallback((input: AddProductInput, quantity = 1) => {
    setIsCartOpen(true); // slide the mini cart out so the shopper sees the item land
    setCart((prev) => {
      // One vendor per cart: adding from a different vendor starts a fresh cart.
      const base: Cart =
        prev && prev.vendorSlug === input.vendorSlug
          ? { ...prev, items: prev.items.map((i) => ({ ...i })) }
          : {
              id: `cart-${Date.now()}`,
              vendorSlug: input.vendorSlug,
              vendorName: input.vendorName,
              items: [],
              total: 0,
              subtotal: 0,
            };

      const idx = base.items.findIndex((i) => i.id === input.productId);
      if (idx >= 0) {
        base.items[idx].quantity += quantity;
      } else {
        base.items.push({
          id: input.productId,
          title: input.name,
          quantity,
          unit_price: input.price,
          thumbnail: input.image,
          metadata: { vendor_id: input.vendorSlug, vendor_name: input.vendorName },
        });
      }
      const t = totalsOf(base.items);
      const next = { ...base, total: t.total, subtotal: t.subtotal };
      saveCart(next);
      return next;
    });
  }, []);

  const updateItem = useCallback((itemId: string, quantity: number) => {
    setCart((prev) => {
      if (!prev) return prev;
      const items = prev.items
        .map((i) => (i.id === itemId ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0);
      if (!items.length) {
        saveCart(null);
        return null;
      }
      const t = totalsOf(items);
      const next = { ...prev, items, total: t.total, subtotal: t.subtotal };
      saveCart(next);
      return next;
    });
  }, []);

  const removeItem = useCallback((itemId: string) => updateItem(itemId, 0), [updateItem]);

  const clearCart = useCallback(() => {
    saveCart(null);
    setCart(null);
  }, []);

  const itemCount = cart ? cart.items.reduce((n, i) => n + i.quantity, 0) : 0;

  return (
    <CartContext.Provider value={{ cart, loading: false, addItem, updateItem, removeItem, clearCart, itemCount, isCartOpen, openCart, closeCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
